/**
 * ChromeOrb.tsx — Iridescent rotating ring orb for Orbi UI
 * =========================================================
 *
 * Replaces VibeOrb.tsx as a drop-in replacement with the same props.
 * Uses the canvas-based chrome orb engine extracted verbatim from
 * orbi-prototype-final.html (approved visual reference).
 *
 * Architecture:
 *   - DPR-aware <canvas> sized to fill its container
 *   - requestAnimationFrame render loop with cleanup on unmount
 *   - LERP-smoothed transitions between all 5 states (t=0.05)
 *   - 4 rotating ring layers × 3 ring sets = 12 iridescent rings
 *   - Graduated line widths: maximised 4/3/2/1px, minimised 2/2/1/1px
 *   - Error state renders static concentric mauve rings
 *
 * States:
 *   idle       → slow rotation, mint hue 172, scale 0.95
 *   listening  → medium speed, mint hue 175, scale 0.95
 *   processing → fast spin, tight mint hue 162, scale 0.95
 *   speaking   → full spectrum (360°), audio-reactive scale
 *   error      → stopped, pink #E8A0E8 concentric rings
 *
 * Props (same as VibeOrb):
 *   state      → OrbState ('idle' | 'listening' | 'thinking' | 'responding' | 'error')
 *   audioLevel → number 0–1 (volume for speaking reactivity)
 *   className  → optional CSS class for the wrapper div
 *
 * File: src/components/ChromeOrb.tsx
 * Last updated: April 4, 2026
 * Do NOT modify the drawOrb core algorithm — it matches the approved prototype.
 */

import React, { useRef, useEffect, useCallback } from 'react';
import type { OrbState } from '../types';

/* ── Props interface (drop-in replacement for VibeOrb) ── */
interface ChromeOrbProps {
  /** Current orb visual state */
  state: OrbState;
  /** Audio volume level 0–1 for speaking reactivity */
  audioLevel: number;
  /** Optional CSS class for wrapper div */
  className?: string;
  /** Ring size multiplier — 1.0 for main orb, 0.9 for mini orb (default 1.0) */
  scaleFactor?: number;
}

/* ══════════════════════════════════════════════════════════
   RING GEOMETRY — pre-computed point arrays
   3 ring sets × max points per ring = rotating wireframe sphere
   Extracted verbatim from orbi-prototype-final.html
   ══════════════════════════════════════════════════════════ */
const MAX_POINTS = 80;

/** Build the 3 orthogonal ring point arrays (XY, YZ, ZX planes) */
function buildRingPoints(): number[][] {
  const points: number[][] = [];
  // Ring 1: XY plane
  for (let a = 0; a < MAX_POINTS; a++) {
    const r = (Math.PI * 2 / MAX_POINTS) * a;
    points.push([Math.cos(r), Math.sin(r), 0]);
  }
  // Ring 2: YZ plane (rotate ring 1 by 90° around X)
  for (let a = 0; a < MAX_POINTS; a++) {
    points.push([0, points[a][0], points[a][1]]);
  }
  // Ring 3: ZX plane (rotate ring 1 by 90° around Y)
  for (let a = 0; a < MAX_POINTS; a++) {
    points.push([points[a][1], 0, points[a][0]]);
  }
  return points;
}

// Pre-compute once at module load (immutable geometry)
const RING_POINTS = buildRingPoints();

/* ══════════════════════════════════════════════════════════
   SPEAKING STATE BRAND HUES
   4 hue values cycled around the ring in speaking state.
   Uses same hsla(60%, 65%) rendering as original for vivid colour.
     300° = pink/magenta
      60° = yellow
     155° = mint/green
     225° = lavender/blue
   ══════════════════════════════════════════════════════════ */
const SPEAK_HUES = [300, 60, 155, 225];

/* ── Linear interpolation helper ── */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/* ══════════════════════════════════════════════════════════
   STATE TARGET VALUES
   Each orb state maps to target values for hue, hue range,
   rotation speed, opacity, scale, and line width.
   LERP smooths transitions between states at t=0.05.
   ══════════════════════════════════════════════════════════ */
interface StateTargets {
  hue: number;
  range: number;
  speed: number;
  opacity: number;
  scale: number;
  lineWidth: number;
}

/**
 * Map OrbState (from types.ts) to the internal state names
 * used by the drawOrb engine. VibeOrb used 'thinking' and 'responding';
 * the prototype uses 'processing' and 'speaking'.
 */
function mapOrbState(state: OrbState): string {
  switch (state) {
    case 'thinking':    return 'processing';
    case 'responding':  return 'speaking';
    default:            return state; // idle, listening, error pass through
  }
}

/** Get target values for a given internal state */
function getStateTargets(internalState: string, audioLevel: number): StateTargets {
  switch (internalState) {
    case 'dormant':
      // Alive but not switched on — slow rotation, dim opacity
      return { hue: 172, range: 30, speed: 0.5, opacity: 0.06, scale: 0.95, lineWidth: 1.1 };
    case 'idle':
      return { hue: 172, range: 30, speed: 2.2, opacity: 0.10, scale: 0.95, lineWidth: 1.1 };
    case 'listening':
      return { hue: 175, range: 18, speed: 1.4, opacity: 0.16, scale: 0.95, lineWidth: 0.95 };
    case 'processing':
      return { hue: 162, range: 6, speed: 3.0, opacity: 0.2, scale: 0.95, lineWidth: 1.1 };
    case 'speaking':
      // Full spectrum, audio-reactive scale
      return { hue: 0, range: 360, speed: 3.0, opacity: 0.18, scale: 0.95 + (audioLevel * 0.18), lineWidth: 1.0 };
    case 'error':
      return { hue: 310, range: 20, speed: 0, opacity: 0.0, scale: 0.7, lineWidth: 1.4 };
    default:
      return { hue: 172, range: 25, speed: 0.28, opacity: 0.11, scale: 0.95, lineWidth: 0.75 };
  }
}

/* ══════════════════════════════════════════════════════════
   DRAW ORB — core render function
   Extracted verbatim from orbi-prototype-final.html drawOrb().
   Renders one frame of the rotating iridescent ring system.

   Parameters:
     canvas      — the <canvas> element
     ctx         — its 2D rendering context
     scaleFactor — multiplier for ring size (1.0 for main, 0.9 for mini)
     current     — mutable state object (LERP'd values persist across frames)
     internalState — mapped state string
     audioLevel  — 0–1 volume
   ══════════════════════════════════════════════════════════ */
interface OrbRenderState {
  hue: number;
  speed: number;
  opacity: number;
  scale: number;
  lineWidth: number;
  count: number;
}

function drawOrb(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  scaleFactor: number,
  current: OrbRenderState,
  internalState: string,
  audioLevel: number
): void {
  // Get target values for current state
  const targets = getStateTargets(internalState, audioLevel);
  const t = 0.05; // LERP rate — smooth transitions

  // LERP all values toward targets
  current.hue     = lerp(current.hue, targets.hue, t * 2);
  current.speed   = lerp(current.speed, targets.speed, t);
  current.opacity = lerp(current.opacity, targets.opacity, t);
  current.scale   = lerp(current.scale, targets.scale, t);
  current.lineWidth = lerp(current.lineWidth, targets.lineWidth, t);
  current.count  += current.speed;

  const W = canvas.width;
  const H = canvas.height;
  const cx = W / 2;
  const cy = H / 2;

  // Trail fade for non-error states; clear for error
  ctx.globalCompositeOperation = 'source-over';
  if (internalState === 'error') {
    ctx.clearRect(0, 0, W, H);
  } else {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = 'rgba(0,0,0,0.08)';
    ctx.fillRect(0, 0, W, H);
  }
  ctx.globalCompositeOperation = 'lighter';

  const sz = W * 0.28 * current.scale * scaleFactor;
  let tim = current.count / 5;

  // ── Normal orb: 4 nested ring layers ──
  // Per-layer base line widths (outer → inner):
  //   Maximised (scaleFactor 1.0): 2px, 6px, 8px, 10px
  //   Minimised (scaleFactor 0.9): 1px, 1px, 2px, 2px
  const layerWidths = scaleFactor >= 1.0
    ? [2.0, 6.0, 8.0, 10.0]
    : [1.0, 1.0, 2.0, 2.0];
  if (internalState !== 'error') {
    for (let e = 0; e < 4; e++) {
      tim *= 1.7;
      const s = 1 - e / 4;

      // Rotation angles (two-axis rotation matrix)
      let a = tim / 59;
      const yp = Math.cos(a);
      const yp2 = Math.sin(a);
      a = tim / 23;
      const xp = Math.cos(a);
      const xp2 = Math.sin(a);

      // Transform all points through rotation
      const p2: number[][] = [];
      for (let i = 0; i < RING_POINTS.length; i++) {
        const x = RING_POINTS[i][0];
        const y = RING_POINTS[i][1];
        let z = RING_POINTS[i][2];

        // Rotate around Y axis
        const y1 = y * yp + z * yp2;
        const z1 = y * yp2 - z * yp;
        // Rotate around X axis
        const x1 = x * xp + z1 * xp2;
        z = x * xp2 - z1 * xp;

        // Perspective projection (depth scaling)
        const z2 = Math.pow(2, z * s);
        p2.push([x1 * z2, y1 * z2, z]);
      }

      // 10% accent fill circle behind each ring layer (maximised only)
      if (scaleFactor >= 1.0) {
        const dSzFill = sz * s;
        const fillHue = internalState === 'speaking'
          ? (current.count * 0.6) % 360
          : current.hue;
        ctx.beginPath();
        ctx.arc(cx, cy, dSzFill * 0.85, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${fillHue},60%,65%,${current.opacity * 0.1})`;
        ctx.fill();
      }

      // Draw line segments for each of the 3 ring sets
      const dSz = sz * s;
      for (let d = 0; d < 3; d++) {
        for (let i = 0; i < MAX_POINTS; i++) {
          const b = p2[d * MAX_POINTS + i];
          const c2 = p2[((i + 1) % MAX_POINTS) + d * MAX_POINTS];

          // Stroke colour: cycle brand hues in speaking, constrained hue otherwise
          let strokeStyle: string;
          if (internalState === 'speaking') {
            const n = SPEAK_HUES.length;
            const pos = (i / MAX_POINTS * n + current.count * 0.02) % n;
            const idx = Math.floor(pos);
            const ht = pos - idx;
            const h1 = SPEAK_HUES[idx];
            const h2 = SPEAK_HUES[(idx + 1) % n];
            // Shortest-arc interpolation (prevents wrapping through red)
            const diff = ((h2 - h1 + 540) % 360) - 180;
            const hue = (h1 + diff * ht + 360) % 360;
            strokeStyle = `hsla(${hue},60%,65%,${current.opacity})`;
          } else {
            const hue = current.hue - targets.range / 2 + (i / MAX_POINTS) * targets.range;
            strokeStyle = `hsla(${hue},60%,65%,${current.opacity})`;
          }

          ctx.beginPath();
          ctx.strokeStyle = strokeStyle;
          ctx.lineWidth = layerWidths[e] * Math.pow(3, b[2]);
          ctx.moveTo(b[0] * dSz + cx, b[1] * dSz + cy);
          ctx.lineTo(c2[0] * dSz + cx, c2[1] * dSz + cy);
          ctx.stroke();
        }
      }
    }
  }

  // ── Error state: static concentric mauve rings ──
  if (internalState === 'error') {
    const pulse = (Math.sin(Date.now() / 900) + 1) / 2;
    const radii = [0.18, 0.24, 0.30];
    const alphas = [0.55, 0.30, 0.12];

    ctx.globalCompositeOperation = 'lighter';
    radii.forEach((r, i) => {
      ctx.beginPath();
      ctx.arc(cx, cy, W * r, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(232,160,232,${alphas[i] * (0.7 + pulse * 0.3)})`;
      ctx.lineWidth = i === 0 ? 2 : 1;
      ctx.stroke();
    });

    // Small bright core dot (radial gradient)
    const coreGrd = ctx.createRadialGradient(cx, cy, 0, cx, cy, W * 0.06);
    coreGrd.addColorStop(0, 'rgba(232,160,232,0.4)');
    coreGrd.addColorStop(1, 'rgba(232,160,232,0)');
    ctx.fillStyle = coreGrd;
    ctx.beginPath();
    ctx.arc(cx, cy, W * 0.06, 0, Math.PI * 2);
    ctx.fill();
  }
}

/* ══════════════════════════════════════════════════════════
   REACT COMPONENT
   ══════════════════════════════════════════════════════════ */
export const ChromeOrb: React.FC<ChromeOrbProps> = ({ state, audioLevel, className, scaleFactor = 1.0 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);

  /**
   * Mutable render state — persists across frames for LERP smoothing.
   * Initialised to idle defaults so the orb fades in gently on mount.
   */
  const renderState = useRef<OrbRenderState>({
    hue: 172,
    speed: 0.28,
    opacity: 0.16,
    scale: 0.78,
    lineWidth: 1.1,
    count: 150,
  });

  /**
   * Refs for props so the render loop always reads current values
   * without needing to restart the animation on every prop change.
   */
  const stateRef = useRef(state);
  const audioRef = useRef(audioLevel);
  const scaleRef = useRef(scaleFactor);
  stateRef.current = state;
  audioRef.current = audioLevel;
  scaleRef.current = scaleFactor;

  /**
   * Resize the canvas to match its container, accounting for
   * device pixel ratio (DPR) for crisp rendering on retina displays.
   */
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const parent = canvas.parentElement;
    if (!parent) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = parent.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);

    // Only resize if dimensions actually changed (avoids flicker)
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }
  }, []);

  /**
   * Main render loop — runs via requestAnimationFrame.
   * Reads current state/audioLevel from refs, calls drawOrb each frame.
   * Cleans up on unmount to prevent memory leaks.
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initial sizing
    resizeCanvas();

    // Handle window resize (debounced via rAF naturally)
    const handleResize = () => resizeCanvas();
    window.addEventListener('resize', handleResize);

    // The orb's CONTAINER also changes size without a window resize (the ask
    // hero morphs 240 -> 200px when a conversation starts; the voice hero
    // yields to a card). A stale canvas inside the overflow-hidden circle
    // renders as a clipped off-centre crescent, so track the container itself.
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && canvas.parentElement) {
      ro = new ResizeObserver(() => resizeCanvas());
      ro.observe(canvas.parentElement);
    }

    /**
     * Clear canvas on state change to prevent ghosting artifacts
     * from the previous state's trail rendering.
     */
    let prevState = '';

    const loop = () => {
      const internalState = mapOrbState(stateRef.current);

      // Clear canvas when state changes (matches prototype's setOrbState behaviour)
      if (internalState !== prevState) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        prevState = internalState;
      }

      drawOrb(canvas, ctx, scaleRef.current, renderState.current, internalState, audioRef.current);
      animFrameRef.current = requestAnimationFrame(loop);
    };

    animFrameRef.current = requestAnimationFrame(loop);

    // Cleanup: stop animation loop and remove resize listener
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', handleResize);
      if (ro) ro.disconnect();
    };
  }, [resizeCanvas]);

  return (
    <div
      className={className}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          background: 'transparent',
          // Subtle blur + brightness boost matching prototype's CSS filter
          filter: 'blur(0.4px) brightness(1.2)',
        }}
      />
    </div>
  );
};

export default ChromeOrb;
